import {
    TextInput,
    SimpleForm,
    useCreate,
    useNotify,
    Link,
    useRedirect,
    useDataProvider
} from "react-admin";
import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {LocalizationActions} from "../../../Component/Layouts/LocalizationActions";
import {useParams} from "react-router-dom";
import setLoadStatus from "../../../Store/LoaderReducer";
import {useDispatch} from "react-redux";

const LOCALIZATION_DATA = {
    id: '',
    description: '',
    name: '',
    link: '',
    image_url: '',
}

const EMPTY_FORM_DATA = {
    name: '',
    description: '',
    link: '',
    image: '',
};

const hasImage = (image) => {
    if (!image) {
        return false;
    }

    if (image instanceof File) {
        return true;
    }

    return typeof image === 'string' && image.length > 0;
};

const getInputValue = (event) => {
    if (typeof event === 'string') {
        return event;
    }

    return event?.target?.value ?? '';
};

const EventLocalization = () => {
    const { '*': locale, id } = useParams();
    const initialRef = useRef(true);
    const formDataRef = useRef({...EMPTY_FORM_DATA});
    const [localizationData, setLocalizationData] = useState({[locale]: LOCALIZATION_DATA});
    const [formKey, setFormKey] = useState(0);
    const [create] = useCreate();
    const notify = useNotify();
    const redirect = useRedirect();
    const dataProvider = useDataProvider();
    const dispatch = useDispatch();

    useEffect(() => {
        if (!initialRef.current) {
            return;
        }
        initialRef.current = false;

        dataProvider.getOne('events', {id: `${id}/${locale}`}).then((res) => {
            const data = res.data.id !== 0 ? res.data : LOCALIZATION_DATA;
            setLocalizationData({[locale]: data});
            formDataRef.current = {
                name: data.name || '',
                description: data.description || '',
                link: data.link || '',
                image: data.image_url || '',
            };
            setFormKey((current) => current + 1);
        }).catch((error) => {
            notify(error?.message || 'Failed to load event localization', {type: 'warning'});
        });
    }, [dataProvider, id, locale, notify])

    const eventLocalizationCreate = () => {
        const formData = formDataRef.current;

        if (!formData.link.length || !hasImage(formData.image) || !formData.name.length || !formData.description.length) {
            if (!formData.link.length) {
                document.querySelector('.member_link').classList.add('error');
            }

            if (!hasImage(formData.image)) {
                document.querySelector('.image-required').classList.add('error');
            }

            if (!formData.name.length) {
                document.querySelector('.name').classList.add('error');
            }

            if (!formData.description.length) {
                document.querySelector('.description').classList.add('error');
            }

            return;
        }
        dispatch(setLoadStatus(true));
        if (!localizationData[locale].id) {
            create(`events/${id}/${locale}`, {data: {
                    name: formData.name,
                    link: formData.link,
                    description: formData.description,
                    image: formData.image
                }}, {
                onSuccess: (res) => {
                    dispatch(setLoadStatus(false));
                    notify(`Event localization ${locale} created`);
                    redirect(`/events/general/${id}`);
                },
                onError: (res) => {
                    dispatch(setLoadStatus(false));
                    notify(res.message, 'warning');
                }
            });
        }

        if (localizationData[locale].id) {
            dataProvider.update(`events`, {id: `${id}/${locale}`, data: {
                    name: formData.name,
                    link: formData.link,
                    description: formData.description,
                    image: formData.image
                }}).then(() => {
                dispatch(setLoadStatus(false));
                notify(`Event localization ${locale} update`);
                redirect(`/events/general/${id}`);
            }).catch((res) => {
                dispatch(setLoadStatus(false));
                notify(res.message, 'warning');
            });
        }
    }

    const Save = ({...props}) => {
        return (
            <div className="dailyActionToolbar">
                {props.localizationId &&
                    <LocalizationActions id={id} status={props.localizationStatus} url={'events'} redirectUrl={'/events/general'} prefix={locale}/>
                }
                <Link to="/events/general" className="button bar-purple-button">Cancel</Link>
                <button
                    onClick={eventLocalizationCreate}
                    className="button bar-gradient-button"
                    type="button"
                >
                    <span>Save</span>
                </button>
            </div>
        );
    }

    const ImageUploadField = () => {
        const [imageSrc, setImageSrc] = useState(null);
        const existingImage = typeof formDataRef.current.image === 'string' ? formDataRef.current.image : null;

        const uploadImage = async (imageFile) => {
            if (!imageFile) {
                return;
            }

            const mimeType = imageFile.type;
            const mimetypes = ['image/png', 'image/jpeg', 'image/jpg'];

            if (!mimetypes.includes(mimeType)) {
                notify(`Image should be in (.png, .jpeg, .jpg) formats.`, 'warning');
                return;
            }

            if (document.querySelector('.image-required').classList.contains('error')) {
                document.querySelector('.image-required').classList.remove('error');
            }

            formDataRef.current.image = imageFile;

            const reader = new FileReader();

            reader.onload = () => {
                setImageSrc(reader.result);
            }

            reader.readAsDataURL(imageFile);
        }

        return <div className="form-field image-required">
            <span className="fieldTitle">Image</span>
            <div>
                <div className="imageWrap">
                        <img
                            className="image"
                            src={imageSrc || existingImage || ''}
                            alt=""
                        />
                    <p className="required-text">Required</p>
                </div>
                <label className="imageLabel" htmlFor="imageInput"></label>
                <input
                    type="file"
                    id="imageInput"
                    className="fileInput"
                    accept="image/png, image/jpeg, image/jpg"
                    onChange={(e) => uploadImage(e.currentTarget.files[0])}
                />
                <label htmlFor="imageInput" className="uploadButton">
                    {hasImage(formDataRef.current.image) ? 'Update Image' : 'Choose an Image'}
                </label>
            </div>
        </div>;
    }

    const linkChange = (event) => {
        if (document.querySelector('.member_link').classList.contains('error')) {
            document.querySelector('.member_link').classList.remove('error');
        }

        formDataRef.current.link = getInputValue(event);
    }

    const titleChange = (event) => {
        if (document.querySelector('.name').classList.contains('error')) {
            document.querySelector('.name').classList.remove('error');
        }

        formDataRef.current.name = getInputValue(event);
    }
    const descriptionChange = (event) => {
        if (document.querySelector('.description').classList.contains('error')) {
            document.querySelector('.description').classList.remove('error');
        }

        formDataRef.current.description = getInputValue(event);
    }

    return (
        <SimpleForm key={formKey} record={localizationData[locale]} toolbar={<></>}>
                <TextInput className="formFields name" label="Title" name="name"
                           source="name" fullWidth id="name" defaultValue={formDataRef.current.name}
                           onChange={titleChange} multiline/>
                <p className="required-text">Required</p>
                <TextInput className="formFields description" label="Description" name="description"
                           source="description" fullWidth id="description" defaultValue={formDataRef.current.description}
                           onChange={descriptionChange} multiline/>
                <p className="required-text">Required</p>
                <TextInput className="formFields member_link" label="Event link" name="link"
                           source="link" fullWidth id="link" defaultValue={formDataRef.current.link}
                           onChange={linkChange} multiline/>
                <p className="required-text">Required</p>
                <p className="uploadImageLabel"></p>
                <ImageUploadField />
                <Save localizationId={localizationData[locale].id} localizationStatus={localizationData[locale].status} />
        </SimpleForm>
    );
}

export default EventLocalization;
